import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, MapPin, BookOpen, Users, HandCoins, ExternalLink } from "lucide-react";
import api from "../lib/api";

const META = {
  teachers: { title: "Teachers", sub: "Faculty listed by institutes across the city", Icon: Users },
  courses: { title: "Courses", sub: "Courses & fees offered by listed institutes", Icon: BookOpen },
  scholarships: { title: "Scholarships", sub: "Scholarship announcements from institutes", Icon: HandCoins },
};

export default function Explore() {
  const { kind } = useParams();
  const meta = META[kind] || META.courses;
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/explore/${kind}`, { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [kind, q]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <p className="nb-label" style={{ letterSpacing: "0.2em" }}>Explore</p>
      <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight mt-2">{meta.title}</h1>
      <p className="text-slate-600 mt-2 text-sm">{meta.sub}</p>
      <div className="relative mt-6 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input data-testid="explore-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${meta.title.toLowerCase()}...`} className="nb-input" style={{ paddingLeft: 42 }} />
      </div>
      {loading ? <p className="mt-8 text-slate-500 text-sm">Loading...</p> : items.length === 0 ? (
        <div data-testid="explore-empty" className="nb-flat mt-8 p-12 text-center border-dashed text-slate-500">
          <meta.Icon className="w-10 h-10 mx-auto text-slate-400 mb-3" /> Nothing listed here yet.
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => (
            <Link key={it.id} to={`/institutes/${it.institution_id}`} data-testid={`explore-item-${it.id}`} className="nb p-5 bg-white hover:-translate-y-0.5 transition-transform duration-150 flex gap-4">
              {kind === "teachers" && (it.photo_url
                ? <img src={it.photo_url} alt={it.name} className="w-14 h-14 rounded-xl object-cover border-2 border-[#171B33] shrink-0" />
                : <span className="w-14 h-14 rounded-xl border-2 border-[#171B33] flex items-center justify-center font-display font-extrabold text-lg shrink-0" style={{ background: "#A3E635" }}>{it.name?.[0]}</span>)}
              <div className="min-w-0">
                <p className="font-display font-extrabold leading-tight">{it.name || it.title}</p>
                {kind === "teachers" && <p className="text-sm text-slate-600 mt-0.5">{it.subject}{it.experience && ` · ${it.experience}`}</p>}
                {kind === "courses" && <p className="text-sm font-semibold text-slate-600 mt-0.5">{it.fee ? `₹${it.fee}` : "Fee on request"}{it.period && ` · ${it.period}`}</p>}
                {kind === "scholarships" && it.content && <p className="text-sm text-slate-600 mt-1 line-clamp-3">{it.content}</p>}
                <p className="text-xs text-slate-500 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> {it.institution_name} · {it.institution_area}</p>
                {kind === "scholarships" && it.link && <span className="text-xs font-bold text-lime-700 flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> Has application link</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
